import type {
  Address,
  Carer,
  ResidentDtos,
  User,
  UserRole,
  WhoCausedCancellation
} from '@lmc/ts-common'
import type { Skill } from '@lmc/ts-common/lib/rota/dtos/rota-patterns'
import type { MbscResource } from '@mobiscroll/react'
import type { SHIFT } from './constants'

export enum RotaInstanceStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  DUE = 'due',
  IN_PROGRESS = 'in progress',
  OVERDUE = 'overdue',
  DONE = 'done',
  CANCELLED = 'cancelled'
}

export enum OverrideLevel {
  BASE = 'BASE',
  SHIFT_TYPE = 'SHIFT_TYPE',
  VISIT_TYPE = 'VISIT_TYPE'
}

export type RotaResidentSummary = {
  id: string
  name: {
    first: string
    last: string
  }
  picture?: string
  addresses?: ResidentDtos['addresses']
  email?: string
  phoneNumber?: string
  carePackage?: { visitNotes?: string }
  status?: ResidentDtos['status']
}

export type RotaEventInstance = {
  id: string
  homeId: string
  rotaId: string
  subjectId: string
  patternId: string
  siteShift: boolean
  instanceTypeId: string
  instanceType: string
  fundingTypeId: string
  flexiType: boolean
  numberSupply: number
  startTime: string
  endTime: string
  plannedDuration: number
  expectedDuration: string
  orphaned: boolean
  title: string
  addressId: string
  skills: (Skill & { areMatched?: boolean; areAllMatched?: boolean })[]
  rotaTags: string[]
  timezone: string
  status: RotaInstanceStatus
  createdBy: {
    id: string
    name: string
  }
  updatedBy: {
    id: string
    name: string
  }
  teamMembers?: AllocatedTeamMember[]
  clockIns: ClockIn[]
  createdAt: string
  updatedAt: string
  totalResidentMinutes: number
  fundingType: string
  resident: RotaResidentSummary
  address: (Address & { id: string; _id?: string })[]
  deletedServiceUser: string
  modified?: boolean
  isAddressValid?: boolean
  invoicingRateOverride?: unknown // TODO: type this
  description?: string
  cancelledBy?: string
  cancelledReason?: string
  cancelledFinancialImpact?: string
  cancelledPreventable?: boolean
  cancelledNotes?: string
}

export type RotaResidentEventInstance = Partial<RotaEventInstance> & {
  id?: string
  start: string
  end: string
  title?: string
  subjectId: string
  resource: string
  color: string
  deletedSU: boolean
}

export type RotaShiftEventInstance = {
  id: string
  type: typeof SHIFT
  shifts: RotaEventInstance[]
  totalShifts: number
  fullyAllocated: number
}

export type RotaUserEventInstance = Partial<RotaEventInstance> & {
  id?: string
  start: string
  end: string
  title?: string
  subjectId: string
  resource: string[]
  color: string
}

export interface UserResource extends MbscResource {
  user: User
}

export interface ResidentResource extends MbscResource {
  resident?: ResidentDtos
  isDeletedSU: boolean
}

export type RotaBuilderFiltersType = {
  teamMembers?: string[]
  status?: RotaInstanceStatus[]
  serviceUsers?: string[]
  warnings?: string[]
  allocations?: string[]
  roles?: UserRole[]
  overtime?: boolean
  absences?: string[]
  typeOfEvent?: string[]
  flexi?: string[]
  siteShift?: boolean
}

export type RotaBuilderQueryFiltersType = RotaBuilderFiltersType & {
  selectedDate: string
}

export enum START_OVERLAY_TYPE {
  NO_PATTERNS = 'NO_PATTERNS',
  NO_ROTA = 'NO_ROTA'
}

export type ClockInLocation = { lat: number; lng: number; accuracy: number }

export enum GeolocationStatus {
  OUTSIDE_GEOFENCE = 'outside geofence',
  UNAVAILABLE = 'location unavailable',
  ADMIN_OVERRIDE = 'manual clock by admin',
  WITHIN_GEOFENCE = 'within geofence'
}

export type ClockIn = {
  teamMemberId: string
  actualDuration?: number
  actualDurationFormatted?: string
  clockInTime?: string
  clockInTimeSystem?: string
  clockInNotes?: string
  clockedIn?: boolean
  clockOutTime?: string
  clockOutTimeSystem?: string
  clockOutNotes?: string
  clockedOut?: boolean
  clockInGeoLocation?: ClockInLocation
  clockOutGeoLocation?: ClockInLocation
  clockInGeolocationStatus?: GeolocationStatus
  clockOutGeolocationStatus?: GeolocationStatus
  status: string
}

export type ClockInTeamMember = {
  assignedMinutes: number
  role: UserRole
  teamMemberId: string
  carer: Carer
  skills?: {
    skillDetails?: { name: string; selected: boolean }[]
  }
  warnings?: {
    clockOutLate?: number
    clockOutEarly?: number
    clockInLate?: number
    clockInEarly?: number
    clockInMismatch?: number
    clockInLocationMismatch?: boolean
    clockOutLocationMismatch?: boolean
  }
}

export type AllocatedTeamMember = ClockInTeamMember & {
  travelInfo?: TravelInfo
}

export type TravelInfo = {
  fromInstanceId: string
  mode: TravelMode
  distanceText?: string
  distanceMeters?: number
  systemDistanceMeters?: number
  distanceInMiles?: number
  travelTimeText?: string
  travelTimeMinutes?: number
  status: GoogleAPiStatus
  error?: string
  errorCode:
    | GoogleDistanceApiStatus
    | GoogleDistanceElementStatus
    | InternalDistanceError
    | null
  fromAddress?: Address
  updatedBy?: string
}

export type TravelModeInfo = {
  name: TravelMode
  value: TravelMode
  icon: string
  enabled: boolean
}

export enum TravelMode {
  DRIVING = 'driving',
  WALKING = 'walking',
  BICYCLING = 'bicycling',
  TRANSIT = 'transit',
  PASSENGER = 'passenger'
}

export enum GoogleAPiStatus {
  SUCCESS = 'SUCCESS',
  FAILED = 'FAILED',
  QUEUED = 'QUEUED'
}

export enum InternalDistanceError {
  SAME_ORIGIN_DESTINATION = 'SAME_ORIGIN_DESTINATION',
  CONFIGURATION_ERROR = 'CONFIGURATION_ERROR',
  UNKNOWN = 'UNKNOWN'
}

export enum GoogleDistanceApiStatus {
  OK = 'OK',
  INVALID_REQUEST = 'INVALID_REQUEST',
  MAX_ELEMENTS_EXCEEDED = 'MAX_ELEMENTS_EXCEEDED',
  MAX_DIMENSIONS_EXCEEDED = 'MAX_DIMENSIONS_EXCEEDED',
  OVER_DAILY_LIMIT = 'OVER_DAILY_LIMIT',
  OVER_QUERY_LIMIT = 'OVER_QUERY_LIMIT',
  REQUEST_DENIED = 'REQUEST_DENIED',
  UNKNOWN_ERROR = 'UNKNOWN_ERROR',
  ZERO_RESULTS = 'ZERO_RESULTS',
  NOT_FOUND = 'NOT_FOUND'
}

export enum GoogleDistanceElementStatus {
  OK = 'OK',
  ZERO_RESULTS = 'ZERO_RESULTS',
  NOT_FOUND = 'NOT_FOUND',
  MAX_ROUTE_LENGTH_EXCEEDED = 'MAX_ROUTE_LENGTH_EXCEEDED'
}

export type AllocationMember = {
  id: string
  name: { first: string; last: string }
  avatar?: string
  picture?: string
  active?: boolean
  selected?: boolean
  clockedIn?: boolean
  clockedOut?: boolean
  clockedInToOtherInstance?: boolean
  absences?: { name: string }[]
  availabilityPreference?: boolean
  allocated?: RotaEventInstance[]
  careTeam?: string
  role?: string
  email?: string
  phoneNumber?: string
  assignedMinutes?: number
  contractedHoursPerWeek?: number
  workProfile?: {
    contractedHoursPerWeek?: number
    canDoOvertime?: boolean
    rotaPreferenceNotes?: string
  }
}

export type TemplateRota = {
  _id: string
  templateAlias: string
}

export type TodayMonitoringWarnings = {
  totalWarnings: number
  genderMismatch?: boolean
  [key: string]: number | boolean | undefined
}

export type TodayMonitoringStatusSummary = {
  total: number
  statuses: Record<string, number>
  unPublishedChanges?: number
}

export type TodayMonitoring = {
  hasRotaPatterns?: boolean
  totalExpectedClockedIns?: number
  statusSummary?: TodayMonitoringStatusSummary
  clockedInMembers?: MonitoringClockedInEntry[]
  pendingClockIns?: MonitoringClockedInEntry[]
  absentMembers?: MonitoringUser[]
  warnings?: TodayMonitoringWarnings
}

export type RotaOverviewTeamMemberStat = {
  id: string
  name: string
  picture?: string
  weeklyContractedMinutes: number
  assignedMinutes: number
  workedMinutes: number
  underutilisedMinutes: number
  absentDays: number
  overtimeMinutes: number
  exceptionCount: number
  travelMinutes: number
  distanceMeters: number
  totalVisitGaps: number
}

export type RotaOverviewSummary = {
  utilisationAndAttendance: {
    hoursWorked: number
    assignedHours: number
    absentDays: number
    underutilisedMinutes: number
    overtimeMinutes: number
  }
  travelAndGap: {
    travelMinutes: number
    distanceMeters: number
    totalVisitGaps: number
  }
  operationalIssues: {
    exceptions: number
    unallocatedMinutes: number
  }
}

export type RotaOverview = {
  summary: RotaOverviewSummary
  teamMemberStats: RotaOverviewTeamMemberStat[]
}

export type MonitoringUser = {
  id: string
  memberId?: string
  name: { first: string; last: string }
  picture?: string
  absenceType?: string
}

export type MonitoringEvent = {
  id: string
  siteShift?: boolean
  title?: string
  startTime: string
  endTime: string
  timezone?: string
  expectedDuration?: string
  flexiType?: boolean
  resident?: {
    id: string
    name: { first: string; last: string }
    picture?: string
    address?: unknown
    email?: string
    phoneNumber?: string
    carePackage?: { visitNotes?: string }
  }
  address?: unknown
}

export type MonitoringWarningEntry = {
  count: number
  events?: MonitoringEvent[]
  users?: MonitoringUser[]
}

export type MonitoringClockedInEntry = {
  user: MonitoringUser
  event?: MonitoringEvent
  warnings: ClockInTeamMember['warnings']
  status: string
  clockInTime?: string
  note?: string
  location?: ClockInLocation
}

export type InstanceWarningMaps = Record<
  string,
  {
    clockedInEarly: string[]
    clockedInLate: string[]
    clockedOutEarly: string[]
    clockedOutLate: string[]
    doubleBooked: { instances?: string[]; teamMemberId?: string }[]
    durationMismatched: string[]
    clockInLocationMismatch?: string[]
    clockOutLocationMismatch?: string[]
    missed: string[]
    invalidGeofencingAddresses?: string[]
    notYetClockedIn: string[]
    skills: string[]
    gender: string[]
  }
>

export type CancelRevisionPayload = {
  whoCancelled: WhoCausedCancellation
  reason: string
  financialImpact: string
  preventable?: boolean
  notes?: string
}
